import { motion, useMotionValue, useTransform, animate } from "framer-motion";
import { useEffect, useState } from "react";
import { buttonVariants } from "@cronika/ui";
import { useContactModal } from "@/core/providers/ContactModalContext";
import { useContent } from "@/shared/lib/i18n";

export function Hero() {
  const { openModal } = useContactModal();
  const content = useContent();
  const { hero } = content;

  return (
    <section className="relative min-h-screen flex flex-col lg:items-center lg:justify-center overflow-hidden">
      <img
        src="/background-hero.png"
        alt=""
        className="absolute inset-0 w-full h-full object-cover object-center pointer-events-none select-none"
      />
      <div className="absolute inset-0 bg-ink/30 pointer-events-none" />

      {/* RevShare card — desktop only */}
      <motion.div
        initial={{ opacity: 0, x: -60 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.7, delay: 0.3, ease: "easeOut" }}
        className="hidden lg:block absolute left-[3%] top-[9%] rounded-[24px] border border-white/10 overflow-hidden"
        style={{
          width: 324,
          height: 363,
          background: "rgba(20,20,24,0.55)",
          backdropFilter: "blur(32px)",
          WebkitBackdropFilter: "blur(32px)",
          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08), 0 24px 64px rgba(0,0,0,0.55)",
        }}
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at 40% 60%, rgba(200,120,10,0.18) 0%, transparent 65%)" }}
        />
        <div className="relative flex flex-col items-center h-full px-6 pt-6 pb-6">
          <p className="text-[17px] font-semibold text-white w-full text-center mb-4">RevShare</p>
          <div className="relative flex items-center justify-center flex-1">
            <AnimatedTickRing percent={80} />
            <div className="absolute flex flex-col items-center">
              <CountUp to={80} suffix="%" className="text-[56px] font-bold text-white leading-none" />
            </div>
          </div>
        </div>
      </motion.div>

      {/* CPA card — desktop only */}
      <motion.div
        initial={{ opacity: 0, x: 60 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.7, delay: 0.5, ease: "easeOut" }}
        className="hidden lg:block absolute right-[3%] top-[42%] rounded-[24px] border border-white/10 overflow-hidden"
        style={{
          width: 324,
          height: 363,
          background: "rgba(20,20,24,0.55)",
          backdropFilter: "blur(32px)",
          WebkitBackdropFilter: "blur(32px)",
          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08), 0 24px 64px rgba(0,0,0,0.55)",
        }}
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at 70% 35%, rgba(180,100,20,0.2) 0%, transparent 60%)" }}
        />
        <p className="relative z-10 text-[17px] font-semibold text-white text-center pt-6">CPA</p>
        <div className="absolute inset-0 top-[52px]">
          <AnimatedLineChart id="desktop" />
        </div>
        <div className="absolute bottom-6 left-0 right-0 flex justify-center z-10">
          <CountUp to={135} prefix="$" delay={1.0} className="text-[72px] font-bold text-white leading-none" />
        </div>
      </motion.div>

      {/* Center content */}
      <div className="relative px-[14px] pt-24 pb-0 text-center lg:pt-0 lg:px-4 lg:max-w-[700px] lg:mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="inline-flex items-center gap-2 rounded-full border border-[#f4a425]/10 bg-[#f4a425]/10 px-4 py-2 mb-8"
        >
          <span className="text-[16px] text-[#f4a425]">{hero.badge}</span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="tracking-tight leading-[1.08] font-medium text-[31px] lg:text-[clamp(52px,5.5vw,82px)]"
        >
          <span className="text-white">{hero.titleLead}</span>
          <br />
          <span style={{ color: "#fec201" }}>{hero.titleAccent}</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.35 }}
          className="mt-5 text-[17px] text-muted-fg mx-5 lg:mx-auto leading-relaxed"
        >
          {hero.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-8 lg:mt-10"
        >
          <button
            onClick={openModal}
            className={buttonVariants({
              variant: "glow",
              size: "lg",
              radius: "xl",
              className: "flex w-full lg:w-auto lg:inline-flex gap-3",
            })}
          >
            {hero.cta}
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path
                d="M3.75 9H14.25M14.25 9L10.5 5.25M14.25 9L10.5 12.75"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </motion.div>
      </div>

      {/* Mobile cards — shown below content on small screens */}
      <div className="lg:hidden relative px-[14px] pb-10 mt-8 grid grid-cols-2 gap-3 w-full">
        {/* RevShare */}
        <div
          className="relative rounded-[20px] border border-white/10 overflow-hidden flex flex-col items-center pt-4 pb-4"
          style={{
            background: "rgba(20,20,24,0.55)",
            backdropFilter: "blur(32px)",
            WebkitBackdropFilter: "blur(32px)",
          }}
        >
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: "radial-gradient(ellipse at 40% 60%, rgba(200,120,10,0.18) 0%, transparent 65%)" }}
          />
          <p className="relative text-[13px] font-semibold text-white mb-1">RevShare</p>
          <div className="relative flex items-center justify-center">
            <AnimatedTickRing percent={80} size={150} />
            <div className="absolute flex flex-col items-center">
              <CountUp to={80} suffix="%" className="text-[32px] font-bold text-white leading-none" />
            </div>
          </div>
        </div>

        {/* CPA */}
        <div
          className="relative rounded-[20px] border border-white/10 overflow-hidden"
          style={{
            background: "rgba(20,20,24,0.55)",
            backdropFilter: "blur(32px)",
            WebkitBackdropFilter: "blur(32px)",
            minHeight: 180,
          }}
        >
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: "radial-gradient(ellipse at 70% 35%, rgba(180,100,20,0.2) 0%, transparent 60%)" }}
          />
          <p className="relative z-10 text-[13px] font-semibold text-white text-center pt-4">CPA</p>
          <div className="absolute inset-0 top-[36px]">
            <AnimatedLineChart id="mobile" />
          </div>
          <div className="absolute bottom-4 left-0 right-0 flex justify-center z-10">
            <CountUp to={135} prefix="$" delay={1.0} className="text-[44px] font-bold text-white leading-none" />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Animated tick ring ── */
function AnimatedTickRing({ percent, size = 260 }: { percent: number; size?: number }) {
  const total = 36;
  const filled = Math.round((percent / 100) * total);
  const startAngle = (7 * Math.PI) / 6;
  const r = 108;
  const innerR = 88;
  const cx = 130,
    cy = 130;

  return (
    <svg width={size} height={size} viewBox="0 0 260 260">
      {Array.from({ length: total }, (_, i) => {
        const angle = startAngle + (i / total) * 2 * Math.PI;
        const x1 = cx + innerR * Math.cos(angle);
        const y1 = cy + innerR * Math.sin(angle);
        const x2 = cx + r * Math.cos(angle);
        const y2 = cy + r * Math.sin(angle);
        const isActive = i < filled;

        return (
          <motion.line
            key={i}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke={isActive ? "#f4a425" : "#2e2e35"}
            strokeWidth="5"
            strokeLinecap="round"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{
              duration: 0.1,
              delay: isActive ? 0.6 + i * 0.022 : 0.6 + filled * 0.022 + (i - filled) * 0.008,
              ease: "easeOut",
            }}
          />
        );
      })}
    </svg>
  );
}

/* ── Animated dotted line chart ── */
function AnimatedLineChart({ id }: { id: string }) {
  const W = 324,
    H = 311;

  const linePath = `
    M 0 180
    L 12 168 L 20 175 L 30 158 L 40 166 L 50 150
    L 60 158 L 70 140 L 82 132 L 90 142 L 100 122
    L 112 112 L 122 104 L 130 116 L 142 96 L 152 106
    L 164 88 L 174 98 L 184 80 L 196 86 L 206 92
    L 216 82 L 228 90 L 238 100 L 248 92 L 258 102
    L 268 94 L 280 104 L 290 96 L 302 106 L 312 98
    L 324 108
  `;

  const areaPath = `
    M 0 180
    L 12 168 L 20 175 L 30 158 L 40 166 L 50 150
    L 60 158 L 70 140 L 82 132 L 90 142 L 100 122
    L 112 112 L 122 104 L 130 116 L 142 96 L 152 106
    L 164 88 L 174 98 L 184 80 L 196 86 L 206 92
    L 216 82 L 228 90 L 238 100 L 248 92 L 258 102
    L 268 94 L 280 104 L 290 96 L 302 106 L 312 98
    L 324 108
    L 324 ${H} L 0 ${H} Z
  `;

  const filterId = `dotGlow-${id}`;
  const areaClipId = `areaClip-${id}`;
  const amberClipId = `amberClip-${id}`;

  return (
    <svg width="100%" height="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
      <defs>
        <filter id={filterId}>
          <feGaussianBlur stdDeviation="1.5" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <clipPath id={areaClipId}>
          <motion.rect
            x="0"
            y="0"
            height={H}
            initial={{ width: 0 }}
            animate={{ width: W }}
            transition={{ duration: 2.0, delay: 0.7, ease: "easeInOut" }}
          />
        </clipPath>
      </defs>

      <path d={areaPath} fill="rgba(140,140,140,0.15)" clipPath={`url(#${areaClipId})`} />
      <path d={linePath} fill="none" stroke="#4a4a55" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />

      <clipPath id={amberClipId}>
        <motion.rect
          x="0"
          y="0"
          height={H}
          initial={{ width: 0 }}
          animate={{ width: W / 2 }}
          transition={{ duration: 1.5, delay: 0.7, ease: "easeInOut" }}
        />
      </clipPath>
      <path
        d={linePath}
        fill="none"
        stroke="#f4a425"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray="1 2"
        filter={`url(#${filterId})`}
        clipPath={`url(#${amberClipId})`}
      />

      <motion.circle
        cx={162}
        cy={88}
        r="5"
        fill="#f4a425"
        filter={`url(#${filterId})`}
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3, delay: 2.2, ease: "backOut" }}
      />
    </svg>
  );
}

/* ── Counting number animation ── */
function CountUp({
  to,
  prefix = "",
  suffix = "",
  delay = 0.6,
  className,
}: {
  to: number;
  prefix?: string;
  suffix?: string;
  delay?: number;
  className?: string;
}) {
  const mv = useMotionValue(0);
  const rounded = useTransform(mv, (v) => `${prefix}${Math.round(v)}${suffix}`);
  const [display, setDisplay] = useState(`${prefix}0${suffix}`);

  useEffect(() => {
    const unsub = rounded.on("change", setDisplay);
    const ctrl = animate(mv, to, { duration: 1.4, delay, ease: "easeOut" });
    return () => {
      unsub();
      ctrl.stop();
    };
  }, []);

  return <span className={className}>{display}</span>;
}
